import { Directive, forwardRef, Input } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { AuthorizationService } from '../services/authorization.service';
import { AuthorizeRegisterBase } from './authorize-register.base';
import { AUTHORIZERS } from './authorizer';

/**
 * Can use this as
 * - `<ng-template authorizeShow [authorizeRegisterAny]="permissions"></ng-template>`
 *   RegisterId is implicit (from route)
 * - `<ng-template authorizeShow [authorizeRegisterAny]="permissions" [authorizeRegisterId]="registerId"></ng-template>`
 *   RegisterId is explicit
 * - `<ng-template [authorizeShowElse]="TemplateRef" [authorizeRegisterAny]="permissions"></ng-template>`
 *   Show an else template if register is not authorised
 * - `<button authorizeEnable [authorizeRegisterAny]="permissions"></button>`
 *   Disable the element instead of hiding it.
 *   Overrides else.
 */
@Directive({
  selector: '[authorizeRegisterAny]',
  providers: [
    {
      provide: AUTHORIZERS,
      useExisting: forwardRef(() => AuthorizeRegisterAnyDirective),
      multi: true,
    },
  ],
  standalone: false,
})
export class AuthorizeRegisterAnyDirective extends AuthorizeRegisterBase {
  @Input('authorizeRegisterAny') permissions?: RegisterPermissionType[];
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('authorizeRegisterId') registerId?: number;

  constructor(authorizationService: AuthorizationService, router: Router) {
    super(authorizationService, router);
  }
  protected isAuthorizedForRegister(registerId: number) {
    if (!registerId) {
      return of(false);
    }

    const permissions = this.permissions || [];
    if (permissions.length === 0) {
      return of(true);
    }

    return combineLatest(
      permissions.map(permission => super.isAuthorizedForPermission(registerId, permission)),
    ).pipe(map(isAuthorizedResults => isAuthorizedResults.some(x => !!x)));
  }
}
